/*
 * Copyright (c) 2019 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can/util/util';
import Component from 'can/component/component';
import template from './row-channel.stache';
import dateTimeUtil from 'scripts/utils/date-time';
import rowViewModelMixin from '../row/row.view-model.mixin';

const rowChannelViewModel = rowViewModelMixin.extend({
    define: {
        channelEvents: {
            Type: can.List,
            Value: can.List,
        },
        maximumDuration: {
            type: 'number',
        },
        channelData: {
            get() {
                return this.attr('channelEvents')[0];
            },
        },
        cellsData: {
            get() {
                const cellsData = new can.List([]);
                const minDate = this.attr('minDate');
                const events = this.attr('channelEvents');
                const firstEvent = this.attr('channelEvents')[0];
                const totalWidthInHours = this.attr('maximumDuration');
                let remainingTotalWidthInHours = totalWidthInHours;

                // add first empty cell if needed
                const timeDiffToFirstEvent = dateTimeUtil.timeDiffPreciseInHours(
                    minDate,
                    firstEvent.startDate,
                );
                if (timeDiffToFirstEvent > 0) {
                    const cellWidthRatio =
                        timeDiffToFirstEvent / totalWidthInHours;
                    cellsData.push(
                        this.getBasicCell({
                            widthRatio: cellWidthRatio,
                        }),
                    );
                    remainingTotalWidthInHours -= timeDiffToFirstEvent;
                }

                events.each((event, index) => {
                    const eventDuration = dateTimeUtil.timeDiffPreciseInHours(
                        event.startDate,
                        event.endDate,
                    );
                    const cellWidthRatio = eventDuration / totalWidthInHours;
                    event.attr('cellType', 'channel');
                    event.attr(
                        'cellWidthPercentage',
                        cellWidthRatio * 100 + '%',
                    );
                    if (cellWidthRatio < 0.2) {
                        event.attr('cellMini', true);
                    }
                    cellsData.push(event);
                    remainingTotalWidthInHours -= eventDuration;

                    if (index !== events.length - 1) {
                        const nextEvent = events[index + 1];
                        const timeBetweenEvents = dateTimeUtil.timeDiffPreciseInHours(
                            event.endDate,
                            nextEvent.startDate,
                        );
                        if (timeBetweenEvents > 0) {
                            cellsData.push(
                                this.getBasicCell({
                                    widthRatio:
                                        timeBetweenEvents / totalWidthInHours,
                                }),
                            );
                        }
                        remainingTotalWidthInHours -= timeBetweenEvents;
                    }
                });

                if (remainingTotalWidthInHours > 0) {
                    cellsData.push(
                        this.getBasicCell({
                            widthRatio:
                                remainingTotalWidthInHours / totalWidthInHours,
                        }),
                    );
                }
                return cellsData;
            },
        },
    },
});

Component.extend({
    tag: 'viewing-schedule-table-row-channel',
    template: template,
    leakScope: false,
    viewModel: rowChannelViewModel,
});
